import React, { useState, useEffect } from 'react';
import { useDeviceSecurityScan, getSecurityAlerts } from '../utils/deviceScanner';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { 
  Shield, 
  ShieldAlert, 
  ShieldCheck, 
  ShieldX, 
  Monitor, 
  Eye, 
  Puzzle, 
  AlertTriangle,
  RefreshCw,
  CheckCircle,
  XCircle,
  Camera,
  Mic,
  Clipboard
} from 'lucide-react';

export const SecurityScannerDialog = ({ isOpen, onClose }) => {
  const securityData = useDeviceSecurityScan();
  const [isScanning, setIsScanning] = useState(false);
  const alerts = getSecurityAlerts(securityData);

  const handleRescan = async () => {
    setIsScanning(true);
    await securityData.rescan();
    setTimeout(() => setIsScanning(false), 1500);
  };

  const getThreatIcon = () => {
    switch (securityData.threatLevel.level) {
      case 'critical': return <ShieldX className="w-12 h-12 text-red-500" />;
      case 'warning': return <ShieldAlert className="w-12 h-12 text-amber-500" />;
      case 'caution': return <Shield className="w-12 h-12 text-yellow-500" />;
      default: return <ShieldCheck className="w-12 h-12 text-emerald-500" />;
    }
  };

  const getThreatMessage = () => {
    switch (securityData.threatLevel.level) {
      case 'critical': return 'Critical threats detected!';
      case 'warning': return 'Potential threats detected';
      case 'caution': return 'Minor concerns found';
      default: return 'No threats detected';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Device Security Scanner
          </DialogTitle>
          <DialogDescription>
            Scan your device for potential spyware and security threats
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Threat Level Overview */}
          <div className={`p-6 rounded-xl ${securityData.threatLevel.bgColor} border border-${securityData.threatLevel.level === 'secure' ? 'emerald' : securityData.threatLevel.level === 'critical' ? 'red' : 'amber'}-500/30`}>
            <div className="flex items-center gap-4">
              {getThreatIcon()}
              <div>
                <h3 className={`text-xl font-semibold ${securityData.threatLevel.color}`}>
                  {getThreatMessage()}
                </h3>
                <p className="text-sm text-slate-400 mt-1">
                  {securityData.scanComplete ? 'Scan complete' : 'Scanning...'}
                </p>
              </div>
            </div>
          </div>

          {/* Scan Results */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-slate-300">Scan Results</h4>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleRescan}
                disabled={isScanning}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isScanning ? 'animate-spin' : ''}`} />
                Rescan
              </Button>
            </div>

            <ScrollArea className="h-64">
              <div className="space-y-3">
                {/* Screen Recording Check */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                  <div className="flex items-center gap-3">
                    <Monitor className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-white">Screen Recording</p>
                      <p className="text-xs text-slate-500">Detecting capture APIs</p>
                    </div>
                  </div>
                  {securityData.isScreenBeingRecorded ? (
                    <div className="flex items-center gap-2 text-red-400">
                      <XCircle className="w-4 h-4" />
                      <span className="text-xs">Detected</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-emerald-400">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-xs">Clear</span>
                    </div>
                  )}
                </div>

                {/* Extension Check */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                  <div className="flex items-center gap-3">
                    <Puzzle className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-white">Browser Extensions</p>
                      <p className="text-xs text-slate-500">~{securityData.extensionCount} extensions detected</p>
                    </div>
                  </div>
                  {securityData.suspiciousExtensions.length > 0 ? (
                    <div className="flex items-center gap-2 text-amber-400">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-xs">{securityData.suspiciousExtensions.length} suspicious</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-emerald-400">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-xs">Clear</span>
                    </div>
                  )}
                </div>

                {/* Camera Access */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                  <div className="flex items-center gap-3">
                    <Camera className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-white">Camera Access</p>
                      <p className="text-xs text-slate-500">Permission status</p>
                    </div>
                  </div>
                  {securityData.cameraAccess ? (
                    <div className="flex items-center gap-2 text-amber-400">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-xs">Granted</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-emerald-400">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-xs">Denied</span>
                    </div>
                  )}
                </div>

                {/* Microphone Access */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                  <div className="flex items-center gap-3">
                    <Mic className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-white">Microphone Access</p>
                      <p className="text-xs text-slate-500">Permission status</p>
                    </div>
                  </div>
                  {securityData.microphoneAccess ? (
                    <div className="flex items-center gap-2 text-blue-400">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-xs">For calls</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-emerald-400">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-xs">Denied</span>
                    </div>
                  )}
                </div>

                {/* Clipboard Access */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                  <div className="flex items-center gap-3">
                    <Clipboard className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-white">Clipboard Access</p>
                      <p className="text-xs text-slate-500">Can read clipboard</p>
                    </div>
                  </div>
                  {securityData.clipboardAccess ? (
                    <div className="flex items-center gap-2 text-amber-400">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-xs">Granted</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-emerald-400">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-xs">Denied</span>
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
          </div>

          {/* Alerts */}
          {alerts.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-slate-300">Security Alerts</h4>
              <div className="space-y-2">
                {alerts.map(alert => (
                  <div 
                    key={alert.id}
                    className={`p-3 rounded-lg border ${
                      alert.type === 'critical' 
                        ? 'bg-red-500/10 border-red-500/30' 
                        : 'bg-amber-500/10 border-amber-500/30'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <AlertTriangle className={`w-4 h-4 mt-0.5 ${
                        alert.type === 'critical' ? 'text-red-400' : 'text-amber-400'
                      }`} />
                      <div>
                        <p className={`text-sm font-medium ${
                          alert.type === 'critical' ? 'text-red-400' : 'text-amber-400'
                        }`}>
                          {alert.title}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">{alert.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
            <h4 className="text-sm font-medium text-blue-400 mb-2">Recommendations</h4>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>• Disable unnecessary browser extensions</li>
              <li>• Use Government Shield desktop app for enhanced security</li>
              <li>• Avoid using public/shared computers</li>
              <li>• Keep your operating system updated</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Compact security indicator for the sidebar
export const SecurityIndicator = ({ onClick }) => {
  const securityData = useDeviceSecurityScan();
  
  return (
    <button 
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${securityData.threatLevel.bgColor} border border-${securityData.threatLevel.level === 'secure' ? 'emerald' : 'amber'}-500/30`}
      data-testid="security-indicator"
    >
      {securityData.threatLevel.level === 'secure' ? (
        <ShieldCheck className="w-4 h-4 text-emerald-400" />
      ) : securityData.threatLevel.level === 'critical' ? (
        <ShieldX className="w-4 h-4 text-red-400" />
      ) : (
        <ShieldAlert className="w-4 h-4 text-amber-400" />
      )}
      <span className={`text-xs font-medium ${securityData.threatLevel.color}`}>
        {securityData.threatLevel.level === 'secure' ? 'Secure' : 'Check'}
      </span>
    </button>
  );
};
